'use client';

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import clsx from 'clsx';
import type { CustomerReviewsProps } from './types';
import Text, { TextTag, TEXT_VARIANT } from '@pixum/text';
import Review from '@pixum/review';
import Divider from '@pixum/divider';
import Pagination from '@pixum/pagination';
import { Icon } from '@pixum/icon';
import { Resume, Comment } from './partials';
import { type ReviewNode, type ProductSpecificationNode, type Rating } from 'data-layer';
import { IMGIX_ASSETS_HOST } from 'data-layer/constants';
import { customerReviewsService } from 'services/api/client';
import { useSafeExecution } from 'hooks';
import { objectToString, replaceDynamicConstantByObject, formatShortDate } from 'utils';
import { REVIEW_SECTION_ID } from '../../constants';
import './styles.css';

export function CustomerReviews({
  className,
  count,
  reviews,
  rating,
  ratingDescription,
  anonymousAuthor,
  customerRatings,
  customerRatingAuthenticity,
  ratingStars,
  ratingFrom,
  stats,
  ups,
  locale = 'de-DE',
  authenticityReviewsLink,
  next,
  previous,
  showMoreLabel,
  showLessLabel
}: CustomerReviewsProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [currentReviews, setCurrentReviews] = useState<ReviewNode[]>(reviews);
  const [totalReviewsCount, setTotalReviewsCount] = useState(count);
  const [selectedRating, setSelectedRating] = useState<number | undefined>(undefined);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [firstReview, setFirstReview] = useState(0);
  const [lastReview, setLastReview] = useState(Math.min(reviews.length, count));
  const reviewsPerPage = 9;

  const prevSelectedRating = useRef(selectedRating);

  const { execute } = useSafeExecution();

  const formatDate = (timestamp: string | undefined) => formatShortDate(timestamp, locale);

  const totalPages = Math.ceil(totalReviewsCount / reviewsPerPage);
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;

  const paginationText = useMemo(
    () =>
      replaceDynamicConstantByObject(ratingFrom, {
        start: String(firstReview + 1),
        end: String(lastReview),
        count: String(totalReviewsCount)
      }),
    [ratingFrom, firstReview, lastReview, totalReviewsCount]
  );

  // Load reviews from API
  const loadReviews = useCallback(
    async (page: number, limit: number, spec?: ProductSpecificationNode, ratingFilter?: number) => {
      const start = (page - 1) * limit;
      const requestData = {
        start,
        limit,
        onlyComments: true,
        setSpecification: objectToString(spec || {}),
        locale,
        ...(ratingFilter && { rating: ratingFilter })
      };
      const reviewsResponse = await customerReviewsService.fetchReviews(requestData);

      if (!reviewsResponse) {
        return { reviews: [], total: 0 };
      }

      const transformedReviews: ReviewNode[] = reviewsResponse.ratings.map((rating: Rating) => ({
        author: rating.author?.trim() || anonymousAuthor,
        comment: rating.comment,
        date: rating.survey_sent_at,
        rating: parseInt(rating.rating, 10),
        productId: parseInt(rating.product_id, 10)
      }));

      let totalCount = reviewsResponse.total_count;

      if (ratingFilter) {
        totalCount = reviewsResponse.rating_stats[ratingFilter.toString()];
      }

      return {
        reviews: transformedReviews,
        total: totalCount
      };
    },
    [anonymousAuthor, locale]
  );

  // Fetch reviews after user interaction
  useEffect(() => {
    if (!hasInteracted) return;

    const fetchData = async () => {
      const result = await execute(
        () => loadReviews(currentPage, reviewsPerPage, ups, selectedRating),
        error => {
          console.error('Failed to load reviews:', error);
        }
      );

      if (result) {
        setCurrentReviews(result.reviews);
        setTotalReviewsCount(result.total);
        setFirstReview((currentPage - 1) * reviewsPerPage);
        setLastReview(Math.min(currentPage * reviewsPerPage, result.total));
      }
    };

    fetchData();
  }, [currentPage, reviewsPerPage, selectedRating, hasInteracted, ups, execute, loadReviews]);

  // Reset to first page when rating filter changes
  useEffect(() => {
    if (hasInteracted && prevSelectedRating.current !== selectedRating) {
      setCurrentPage(1);
    }
    prevSelectedRating.current = selectedRating;
  }, [selectedRating, hasInteracted]);

  const handlePrevClick = () => {
    if (!isFirstPage) {
      setHasInteracted(true);
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextClick = () => {
    if (!isLastPage) {
      setHasInteracted(true);
      setCurrentPage(prev => prev + 1);
    }
  };

  const handleSelectRating = (rating: number) => {
    setHasInteracted(true);
    setSelectedRating(selectedRating === rating ? undefined : rating);
  };

  return (
    <section className={clsx('customer-reviews main-section center--content', className)} id={REVIEW_SECTION_ID}>
      <Text className="customer-reviews__headline headline" tag={TextTag.H2} variant={TEXT_VARIANT.TITLE_2}>
        {customerRatings}
      </Text>
      <div className="customer-reviews__container">
        <div className="customer-reviews__resume">
          <Resume
            totalAmount={count}
            ratingDescription={ratingDescription}
            stats={stats}
            average={rating}
            onSelectRating={handleSelectRating}
            selectedRating={selectedRating}
            ratingStars={ratingStars}
            customerRatingAuthenticityLabel={customerRatingAuthenticity}
            authenticityReviewsLink={authenticityReviewsLink}
          />
        </div>
        <div className="customer-reviews__comments">
          <div className="customer-reviews__columns">
            {currentReviews.map((item: ReviewNode, index: number) => (
              <Review
                id={`review-${item.productId}-${index}`}
                title={item.author}
                date={formatDate(item.date)}
                rating={item.rating}
                comment={<Comment comment={item.comment} showMoreLabel={showMoreLabel} showLessLabel={showLessLabel} />}
                key={`review-${item.productId}-${index}`}
                avatar={
                  <Icon
                    className="customer-reviews__avatar"
                    src={`${IMGIX_ASSETS_HOST}/images/svg/avatar-neutral.svg`}
                    loading="lazy"
                    size="large"
                    useMask={false}
                  />
                }
              />
            ))}
          </div>
          <Divider blockMargin />
          <Pagination
            disableNextButton={isLastPage}
            disablePrevButton={isFirstPage}
            onNextClick={handleNextClick}
            onPrevClick={handlePrevClick}
            position="bottom"
            text={paginationText}
            titleNextButton={next}
            titlePrevButton={previous}
          />
        </div>
      </div>
    </section>
  );
}
